// Agency Performance page — 90-day agency stats and per-carer breakdown.

import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { AgencySummary, CarerTable, type AgencyPerformance, type CarerPerformance } from "../components/PerformanceComponents.js";

export function PerformancePage(): JSX.Element {
  // /performance/agency returns a flat object (total_carers, active_carers_90d,
  // avg_csat_90d, ...) — not an envelope. Leave as-is.
  const agency = useQuery<AgencyPerformance>({
    queryKey: ["performance-agency"],
    queryFn: () => apiFetch<AgencyPerformance>("/performance/agency"),
  });
  // /performance/carers returns the standard { data, total, page, limit }
  // envelope. Use select to unwrap so <CarerTable carers={carers.data} />
  // receives an array rather than the envelope object, which would crash
  // .map at runtime.
  const carers = useQuery<
    { data: CarerPerformance[]; total: number; page: number; limit: number },
    Error,
    CarerPerformance[]
  >({
    queryKey: ["performance-carers"],
    queryFn: () => apiFetch<{ data: CarerPerformance[]; total: number; page: number; limit: number }>("/performance/carers"),
    select: (res) => res.data,
  });

  const isLoading = agency.isLoading || carers.isLoading;
  const isError   = agency.isError   || carers.isError;
  const ready     = !isLoading && !isError;

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        <h1 className="text-xl font-semibold text-slate-900">Agency Performance</h1>

        {isLoading && <p className="py-8 text-center text-sm text-slate-400">Loading…</p>}

        {isError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-6 py-4">
            <p className="text-sm text-red-700 font-medium">Failed to load performance data.</p>
          </div>
        )}

        {ready && agency.data && <AgencySummary d={agency.data} />}
        {ready && carers.data && <CarerTable carers={carers.data} />}

        <p className="text-xs text-slate-400 text-center">Based on rolling 90-day window.</p>
      </div>
    </Shell>
  );
}
